// src/components/catalog/pickers/ProductPicker.jsx
//
// Shared product picker for catalog-sized product lists.
// =============================================================================
//
// PURPOSE
// -------
// This component replaces native product dropdowns in operational screens.
// A native <select> is acceptable for short dictionaries, but it becomes poor UX
// once the salon has tens or hundreds of products. Product selection must support
// both fast work and discovery:
//
// - fast search by name/SKU/barcode for users who know what they need,
// - Browse modal with table view for users who do not know the exact product
//   naming used in the catalog,
// - enough product context to prevent mistakes: stock unit, category, stock and
//   price/cost fields supplied by the backend context endpoint.
//
// ARCHITECTURAL BOUNDARY
// ----------------------
// The picker only selects a product object. It does not post stock movements,
// calculate valuation or create sales lines. Stock and Sales screens decide what
// to do with the selected product and backend services remain authoritative.
//
// I18N NOTE
// ---------
// This component translates labels, helper text and accessibility copy only.
// The product object returned to parent components stays unchanged and continues
// to use backend field names such as stock_unit_code, sku, barcode and price_gross.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./CatalogPicker.css";

// Format decimal quantities for human-readable tables. The backend keeps numeric
// precision authoritative; this helper only avoids noisy trailing zeroes in the UI.
function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value, currency = "PLN") {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} ${currency}` : String(value);
}

// Product display helpers intentionally accept incomplete product rows. Different
// picker contexts may return different projections, so the UI must stay tolerant.
function productName(product, t) {
  return product?.name_public || product?.name || t("catalog:picker.unnamedProduct");
}

function productSkuLine(product, t) {
  const parts = [
    product?.sku ? t("catalog:picker.sku", { value: product.sku }) : null,
    product?.barcode ? t("catalog:picker.barcode", { value: product.barcode }) : null,
    product?.brand ? t("catalog:picker.brand", { value: product.brand }) : null,
  ].filter(Boolean);

  return parts.join(" · ") || t("catalog:picker.noSkuBarcode");
}

function productCategory(product) {
  return product?.category?.name_public || product?.category?.name || product?.category_name || "—";
}

function stockUnit(product) {
  return product?.stockUnit?.symbol || product?.stock_unit_code || "pcs";
}

function quantityWithUnit(quantity, product) {
  return `${formatDecimal(quantity || 0)} ${stockUnit(product)}`;
}

/**
 * ProductPicker component.
 *
 * Props:
 * - searchProducts(params): async function returning product rows,
 * - selectedProduct: optional currently selected row,
 * - onSelect(product): called when user chooses a product,
 * - searchParams: additional backend params such as location_id,
 * - context: changes labels only; backend data shape stays explicit.
 *
 * The default labels are resolved through i18n, but callers may still override
 * them when a specific screen needs a more precise wording.
 */
const ProductPicker = ({
  label,
  placeholder,
  browseLabel,
  selectedProduct = null,
  onSelect,
  searchProducts,
  searchParams = {},
  disabled = false,
  currency = "PLN",
  context = "stock",
  helperText,
}) => {
  const { t } = useTranslation(["common", "catalog"]);

  const resolvedLabel = label ?? t("catalog:picker.productLabel");
  const resolvedPlaceholder = placeholder ?? t("catalog:picker.productPlaceholder");
  const resolvedBrowseLabel = browseLabel ?? t("catalog:picker.browseProducts");
  const resolvedHelperText = helperText ?? t("catalog:picker.helperText");

  const [query, setQuery] = useState("");
  const [quickResults, setQuickResults] = useState([]);
  const [modalResults, setModalResults] = useState([]);
  const [modalQuery, setModalQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingQuick, setLoadingQuick] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");

  const normalizedSearchParams = useMemo(() => searchParams || {}, [searchParams]);

  // Quick search supports the fast path: barcode/name/SKU entry when the user
  // already knows what they want. It deliberately limits results for readability.
  async function runQuickSearch(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setError("");
    setLoadingQuick(true);

    try {
      const rows = await searchProducts({ ...normalizedSearchParams, q: query.trim() || undefined, limit: 12 });
      setQuickResults(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.message || t("catalog:picker.productSearchFailed"));
    } finally {
      setLoadingQuick(false);
    }
  }

  // Modal search supports the discovery path: a larger table-based product list
  // for users who do not remember the exact catalog naming.
  async function runModalSearch(nextQuery = modalQuery) {
    setError("");
    setLoadingModal(true);

    try {
      const rows = await searchProducts({ ...normalizedSearchParams, q: String(nextQuery || "").trim() || undefined, limit: 100 });
      setModalResults(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.message || t("catalog:picker.productBrowseSearchFailed"));
    } finally {
      setLoadingModal(false);
    }
  }

  function openModal() {
    setModalOpen(true);
    setModalQuery(query);
    runModalSearch(query);
  }

  // Selection is the component boundary. We pass the selected product to the
  // parent and close local UI state, but do not modify stock/sales data here.
  function choose(product) {
    onSelect?.(product);
    setQuery(productName(product, t));
    setQuickResults([]);
    setModalOpen(false);
  }

  useEffect(() => {
    if (selectedProduct) setQuery(productName(selectedProduct, t));
  }, [selectedProduct, t]);

  return (
    <div className="catalog-picker catalog-picker--product">
      <label className="catalog-picker__label">
        {resolvedLabel}
        <div className="catalog-picker__row">
          <input
            value={query}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") runQuickSearch(event);
            }}
          />
          <button type="button" className="btn btn-outline-primary" disabled={disabled || loadingQuick} onClick={runQuickSearch}>
            {loadingQuick ? t("catalog:picker.searching") : t("catalog:picker.search")}
          </button>
          <button type="button" className="btn btn-outline-secondary" disabled={disabled} onClick={openModal}>
            {resolvedBrowseLabel}
          </button>
        </div>
      </label>

      {resolvedHelperText && <div className="catalog-picker__hint">{resolvedHelperText}</div>}
      {error && <div className="catalog-picker__error">{error}</div>}

      {selectedProduct && (
        <div className="catalog-picker__selected">
          <strong>{productName(selectedProduct, t)}</strong>
          <span>{productSkuLine(selectedProduct, t)}</span>
          <div className="catalog-picker__badge-row">
            <span className="catalog-picker__badge">{t("catalog:picker.unit")}: {stockUnit(selectedProduct)}</span>
            {selectedProduct.available_quantity !== undefined && (
              <span className="catalog-picker__badge">{t("catalog:picker.available")}: {quantityWithUnit(selectedProduct.available_quantity, selectedProduct)}</span>
            )}
            {selectedProduct.global_stock_quantity !== undefined && (
              <span className="catalog-picker__badge">{t("catalog:picker.global")}: {quantityWithUnit(selectedProduct.global_stock_quantity, selectedProduct)}</span>
            )}
            {context === "sales" && selectedProduct.price_gross !== undefined && (
              <span className="catalog-picker__badge">{t("catalog:picker.price")}: {formatMoney(selectedProduct.price_gross, currency)}</span>
            )}
            {context === "stock" && selectedProduct.average_cost_net !== undefined && (
              <span className="catalog-picker__badge">{t("catalog:picker.avgCost")}: {formatMoney(selectedProduct.average_cost_net, currency)}</span>
            )}
          </div>
        </div>
      )}

      {quickResults.length > 0 && (
        <div className="catalog-picker__quick-results" aria-label={t("catalog:picker.productQuickResultsAriaLabel")}>
          {quickResults.map((product) => (
            <button key={product.id} type="button" className="catalog-picker__quick-button" disabled={disabled} onClick={() => choose(product)}>
              <span className="catalog-picker__quick-main"><strong>{productName(product, t)}</strong><span>{productSkuLine(product, t)}</span></span>
              <span className="catalog-picker__quick-sub">
                <span>{productCategory(product)}</span>
                <span>{t("catalog:picker.stock")}: {quantityWithUnit(product.available_quantity ?? product.global_stock_quantity ?? 0, product)}</span>
              </span>
              <span className="catalog-picker__quick-tail">
                {context === "sales" ? <span>{formatMoney(product.price_gross, currency)}</span> : <span>{formatMoney(product.average_cost_net, currency)}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="catalog-picker__modal-backdrop" role="presentation">
          <div className="catalog-picker__modal" role="dialog" aria-modal="true" aria-label={t("catalog:picker.browseProductsAriaLabel")}>
            <div className="catalog-picker__modal-header">
              <div><h3>{t("catalog:picker.browseProductsTitle")}</h3><p className="catalog-picker__meta">{t("catalog:picker.browseProductsHelp")}</p></div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>{t("common:actions.close")}</button>
            </div>

            <div className="catalog-picker__modal-toolbar">
              <input
                value={modalQuery}
                disabled={loadingModal}
                placeholder={t("catalog:picker.searchProductsPlaceholder")}
                onChange={(event) => setModalQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    runModalSearch();
                  }
                }}
              />
              <button type="button" className="btn btn-outline-primary" disabled={loadingModal} onClick={() => runModalSearch()}>{loadingModal ? t("catalog:picker.searching") : t("catalog:picker.search")}</button>
            </div>

            <div className="catalog-picker__modal-body">
              <table className="catalog-picker__table">
                <thead>
                  <tr>
                    <th>{t("catalog:picker.columns.product")}</th>
                    <th>{t("catalog:picker.columns.skuBarcode")}</th>
                    <th>{t("catalog:picker.columns.category")}</th>
                    <th className="catalog-picker__right">{t("catalog:picker.columns.stock")}</th>
                    <th>{t("catalog:picker.columns.unit")}</th>
                    <th className="catalog-picker__right">{context === "sales" ? t("catalog:picker.columns.price") : t("catalog:picker.columns.avgCost")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {modalResults.length === 0 && !loadingModal && <tr><td colSpan="7" className="catalog-picker__empty">{t("catalog:picker.noProducts")}</td></tr>}
                  {modalResults.map((product) => (
                    <tr key={product.id}>
                      <td><strong>{productName(product, t)}</strong>{product.variant_label && <div className="catalog-picker__meta">{product.variant_label}</div>}</td>
                      <td>{productSkuLine(product, t)}</td>
                      <td>{productCategory(product)}</td>
                      <td className="catalog-picker__right">{quantityWithUnit(product.available_quantity ?? product.global_stock_quantity ?? 0, product)}</td>
                      <td>{stockUnit(product)}</td>
                      <td className="catalog-picker__right">{context === "sales" ? formatMoney(product.price_gross, currency) : formatMoney(product.average_cost_net, currency)}</td>
                      <td className="catalog-picker__right"><button type="button" className="btn btn-sm btn-primary" onClick={() => choose(product)}>{t("catalog:picker.select")}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="catalog-picker__modal-footer"><span>{t("catalog:picker.productsShown", { count: modalResults.length })}</span><span>{t("catalog:picker.barcodeTip")}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPicker;




